import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

@Component({
  selector: 'gatewee-upload',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, GateweeIconComponent],
  templateUrl: './gatewee-upload.component.html',
})
export class GateweeUploadComponent {
  @Input({required: true}) control!: FormControl<File | null>;
  @Input() previewControl?: FormControl<string>;
  @Input() label = 'Upload de Imagem';
  @Input() title = 'Escolha sua imagem';
  @Input() subtitle = 'PNG ou JPG. 400x400. Até 1MB.';

  private readonly allowed = ['image/png', 'image/jpeg'];
  private readonly maxBytes = 1 * 1024 * 1024;
  private readonly targetW = 400;
  private readonly targetH = 400;

  loading = false;
  progress = 0;
  errorMsg: string | null = null;

  private progressTimer?: ReturnType<typeof setInterval>;
  private readonly minTimeMs = 3000;

  async onFileChange(
    file: File | null,
    fileInput?: HTMLInputElement,
  ): Promise<void> {
    this.errorMsg = null;
    this.control.markAsTouched();

    if (!file) {
      this.endLoading();

      this.control.setValue(null);
      this.previewControl?.setValue('');
      this.control.setErrors(null);

      if (fileInput) fileInput.value = '';
      return;
    }

    if (!this.allowed.includes(file.type)) {
      this.setError({fileType: true}, fileInput);
      return;
    }

    if (file.size > this.maxBytes) {
      this.setError({fileSize: true}, fileInput);
      return;
    }

    const okDim = await this.checkDim(file, this.targetW, this.targetH);
    if (!okDim) {
      this.setError({fileDim: true}, fileInput);
      return;
    }

    this.loading = true;
    this.control.setErrors(null);
    this.startProgress(this.minTimeMs);

    await this.sleep(this.minTimeMs);

    this.control.setValue(file);
    const url = URL.createObjectURL(file);
    this.previewControl?.setValue(url);

    this.endLoading();

    if (fileInput) fileInput.value = '';
  }

  private setError(err: ValidationErrors, fileInput?: HTMLInputElement): void {
    this.control.setValue(null);
    this.previewControl?.setValue('');
    this.control.setErrors(err);
    this.control.markAsTouched();

    this.errorMsg = this.mapErrorToText(err);

    this.endLoading();

    if (fileInput) fileInput.value = '';
  }

  private mapErrorToText(err: ValidationErrors): string {
    if (err['fileType']) return 'Formato inválido. Envie PNG ou JPG.';
    if (err['fileSize']) return 'Arquivo muito grande. Tamanho máximo: 1MB.';
    if (err['fileDim']) return 'Dimensão inválida. A imagem deve ser 400x400.';
    return 'Imagem inválida.';
  }

  private startProgress(durationMs = 3000): void {
    this.stopProgress();
    this.progress = 0;

    const tickMs = 60;
    const steps = Math.ceil(durationMs / tickMs);
    const inc = 100 / steps;

    this.progressTimer = setInterval(() => {
      this.progress = Math.min(99, this.progress + inc);
    }, tickMs);
  }

  private endLoading(): void {
    this.loading = false;
    this.stopProgress();
    this.progress = 0;
  }

  private stopProgress(): void {
    if (this.progressTimer) clearInterval(this.progressTimer);
    this.progressTimer = undefined;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private checkDim(file: File, w: number, h: number): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const ok = img.width === w && img.height === h;
        URL.revokeObjectURL(url);
        resolve(ok);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;
    });
  }
}
